import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight, Loader2, RefreshCw } from 'lucide-react';
import {
  fetchDebugCaptureStatus,
  fetchDebugSample,
  fetchIntegrationActivity,
  fetchIntegrationAlertChannel,
  fetchWebhookQueueStatus,
  setDebugCapture,
  setIntegrationAlertChannel,
  type DebugCaptureStatus,
  type DebugSample,
  type IntegrationActivity,
  type IntegrationRun,
  type WebhookQueuePage,
} from '../../api/dataApi';
import { AppCard } from '../ui/AppCard';
import { HealthPill } from './HealthPill';
import { useStore } from '../../store/StoreContext';
import { describeRunCounts, formatRunTime, relativeAgo, runSourceLabel, runStatusLabel } from '../../lib/integrationHealth';

const PAGE = 10;

const statusColor: Record<IntegrationRun['status'], string> = {
  success: 'text-emerald-600 dark:text-emerald-400',
  empty: 'text-amber-600 dark:text-amber-400',
  failed: 'text-red-600 dark:text-red-400',
};

// The Activity section of an Integration (roadmap #9 Phase 2): derived
// health, counters, collapsed error groups (with what to do about them) and
// the run list. Owner-only - the endpoint 403s anyone else, so the parent
// only renders this for the owner. Run records are metadata only by design
// (§13) - no raw payload is stored UNLESS the owner explicitly arms opt-in
// debug capture below, which is off by default, self-expires after an hour,
// and redacts anything credential-shaped (see DebugCaptureControl).
export function ActivitySection({
  nickname,
  isWebhook,
  onActivityLoaded,
}: {
  nickname: string;
  // Only a sourceType:"webhook" mapping ever has entries in webhook:ingest
  // - showing the queue card for any other connector would always read
  // "0 pending," which is just noise.
  isWebhook?: boolean;
  onActivityLoaded?: (activity: IntegrationActivity) => void;
}) {
  const [activity, setActivity] = useState<IntegrationActivity | null>(null);
  const [runs, setRuns] = useState<IntegrationRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [capture, setCapture] = useState<DebugCaptureStatus | null>(null);
  const [expandedRun, setExpandedRun] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const a = await fetchIntegrationActivity(nickname, 0, PAGE);
      setActivity(a);
      setRuns(a.runs);
      onActivityLoaded?.(a);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unable to load activity.');
    } finally {
      setLoading(false);
    }
    // Best-effort and independent of the activity load above: an older
    // backend without this endpoint, or any other failure here, should
    // never take down the rest of Activity.
    try {
      setCapture(await fetchDebugCaptureStatus(nickname));
    } catch {
      setCapture(null);
    }
  }, [nickname, onActivityLoaded]);

  useEffect(() => {
    void load();
  }, [load]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const a = await fetchIntegrationActivity(nickname, runs.length, PAGE);
      setRuns((prev) => [...prev, ...a.runs]);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unable to load more runs.');
    } finally {
      setLoadingMore(false);
    }
  };

  if (loading && !activity) {
    return (
      <div className="py-4 text-center text-sm text-black/40 dark:text-white/40">
        <Loader2 size={14} className="inline animate-spin" /> Loading activity…
      </div>
    );
  }
  if (error && !activity) return <p className="text-sm text-red-500">{error}</p>;
  if (!activity) return null;

  const { health, stats } = activity;

  return (
    <div className="flex flex-col gap-3">
      <AppCard className="flex flex-col gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <HealthPill state={health.state} />
          <span className="text-xs text-black/50 dark:text-white/50">{health.reason}</span>
          <div className="flex-1" />
          <button
            type="button"
            onClick={() => void load()}
            aria-label="Refresh activity"
            className="rounded-lg p-1.5 text-black/40 dark:text-white/40 hover:bg-black/5 dark:hover:bg-white/10"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
        <div className="grid grid-cols-3 gap-2 text-center">
          <Stat label="Deliveries" value={String(stats.totalRuns)} />
          <Stat label="Signals" value={String(stats.totalSignals)} />
          <Stat label="Last success" value={relativeAgo(stats.lastSuccessAt)} />
        </div>
        {stats.cadenceSec > 0 && (
          <span className="text-[11px] text-black/40 dark:text-white/40">Runs about every {stats.cadenceSec}s.</span>
        )}
      </AppCard>

      {isWebhook && <WebhookQueueCard nickname={nickname} />}

      {capture && <DebugCaptureControl nickname={nickname} capture={capture} onChange={setCapture} />}

      {activity.errors.length > 0 && (
        <div className="flex flex-col gap-2">
          <span className="text-xs font-semibold text-black/50 dark:text-white/50">What went wrong</span>
          {activity.errors.map((g) => (
            <AppCard key={g.code} className="flex flex-col gap-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-[12px]">{g.code}</span>
                <span className="text-[11px] text-black/40 dark:text-white/40">
                  {g.count}× · {g.retryable ? 'retryable' : 'won’t fix itself'} · last {relativeAgo(g.lastAt)}
                </span>
              </div>
              <span className="text-sm">{g.message}</span>
              <span className="text-xs text-black/55 dark:text-white/55">{g.action}</span>
            </AppCard>
          ))}
        </div>
      )}

      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between text-[11px] text-black/40 dark:text-white/40">
          <span>Recent runs</span>
          <span>
            {runs.length} of {activity.total}
          </span>
        </div>
        {runs.length === 0 ? (
          <p className="text-sm text-black/45 dark:text-white/45 m-0">
            No deliveries recorded yet. Once this integration receives data, every delivery shows up here.
          </p>
        ) : (
          <div className="flex flex-col divide-y divide-black/5 dark:divide-white/5">
            {runs.map((run) => (
              <RunRow
                key={run.id}
                nickname={nickname}
                run={run}
                expanded={expandedRun === run.id}
                onToggle={() => setExpandedRun((prev) => (prev === run.id ? null : run.id))}
              />
            ))}
            {runs.length < activity.total && (
              <button
                type="button"
                onClick={() => void loadMore()}
                disabled={loadingMore}
                className="py-2 text-center text-[11px] text-blue-600 hover:underline disabled:opacity-50"
              >
                {loadingMore ? 'Loading…' : 'Load older runs'}
              </button>
            )}
          </div>
        )}
      </div>
      {error && <p className="text-xs text-red-500 m-0">{error}</p>}
      <HealthAlertsControl />
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col">
      <span className="text-base font-semibold">{value}</span>
      <span className="text-[11px] text-black/40 dark:text-white/40">{label}</span>
    </div>
  );
}

const QUEUE_PAGE = 10;

function fmtIdle(ms: number): string {
  if (ms <= 0) return '—';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.round(s / 60);
  return m < 60 ? `${m}m` : `${Math.round(m / 60)}h`;
}

// This mapping's slice of webhook:ingest's pending (claimed, not yet
// ACK'd) entries - normally empty; see backend's webhookrecv.ListPending
// doc comment. A nonzero, growing count here means deliveries to this
// integration are queued but stuck (a crashed/stalled worker), not
// silently dropped.
function WebhookQueueCard({ nickname }: { nickname: string }) {
  const [page, setPage] = useState<WebhookQueuePage | null>(null);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    (o: number) => {
      setLoading(true);
      setError(null);
      fetchWebhookQueueStatus(nickname, o, QUEUE_PAGE)
        .then(setPage)
        .catch((e) => setError(e instanceof Error ? e.message : 'Unable to load webhook queue status.'))
        .finally(() => setLoading(false));
    },
    [nickname]
  );

  useEffect(() => {
    load(0);
  }, [load]);

  return (
    <AppCard className="flex flex-col gap-2">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-semibold flex-1">Webhook queue</span>
        <span className="text-[11px] text-black/40 dark:text-white/40">{page ? `${page.total} pending` : ''}</span>
        <button
          type="button"
          onClick={() => load(offset)}
          aria-label="Refresh webhook queue"
          className="rounded-lg p-1.5 text-black/40 dark:text-white/40 hover:bg-black/5 dark:hover:bg-white/10"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>
      <p className="text-[11px] text-black/40 dark:text-white/40 m-0">
        Deliveries claimed by a worker but not yet finished. Normally empty - a healthy worker finishes within
        milliseconds of receiving one.
      </p>
      {error && <span className="text-xs text-red-500">{error}</span>}
      {page && page.items.length > 0 && (
        <div className="flex flex-col divide-y divide-black/5 dark:divide-white/5">
          {page.items.map((it) => (
            <div key={it.id} className="flex items-center gap-2 py-1.5 text-xs">
              <span className="font-mono text-[11px] text-black/50 dark:text-white/50 truncate flex-1">{it.consumer}</span>
              <span className="shrink-0 text-black/40 dark:text-white/40">idle {fmtIdle(it.idleMs)}</span>
              <span className="shrink-0 text-black/40 dark:text-white/40">{it.deliveries}× delivered</span>
            </div>
          ))}
        </div>
      )}
      {page && page.total > QUEUE_PAGE && (
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={() => {
              const next = Math.max(0, offset - QUEUE_PAGE);
              setOffset(next);
              load(next);
            }}
            disabled={loading || offset === 0}
            className="rounded-md px-2.5 py-1 border border-black/15 dark:border-white/20 text-[11px] disabled:opacity-50"
          >
            Prev
          </button>
          <button
            type="button"
            onClick={() => {
              const next = offset + QUEUE_PAGE;
              setOffset(next);
              load(next);
            }}
            disabled={loading || offset + QUEUE_PAGE >= page.total}
            className="rounded-md px-2.5 py-1 border border-black/15 dark:border-white/20 text-[11px] disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}
    </AppCard>
  );
}

function countdown(expiresAt: number): string {
  const s = Math.max(0, Math.round((expiresAt - Date.now()) / 1000));
  return s >= 60 ? `${Math.ceil(s / 60)}m` : `${s}s`;
}

// Opt-in raw-payload debug capture (§13: "optional debug samples have a
// short TTL and field-level redaction"). Off by default; arming lasts one
// hour and can't be extended without re-arming - there is no "leave it on"
// state. Only webhook deliveries can be captured (see
// internal/webhookrecv/handler.go) - Remote Write carries only numeric
// samples and an agent scrape never sends its payload to the server at all.
function DebugCaptureControl({
  nickname,
  capture,
  onChange,
}: {
  nickname: string;
  capture: DebugCaptureStatus;
  onChange: (c: DebugCaptureStatus) => void;
}) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggle = async () => {
    setSaving(true);
    setError(null);
    try {
      onChange(await setDebugCapture(nickname, !capture.enabled));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not change debug capture.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppCard className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-semibold flex-1">Raw-payload capture</span>
        <span
          className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
            capture.enabled
              ? 'text-amber-600 dark:text-amber-400 border border-amber-500/40'
              : 'text-black/40 dark:text-white/40 border border-black/10 dark:border-white/15'
          }`}
        >
          {capture.enabled ? `Armed · ${countdown(capture.expiresAt)} left` : 'Off'}
        </span>
        <button
          type="button"
          onClick={() => void toggle()}
          disabled={saving}
          className="rounded-lg border border-black/15 dark:border-white/15 px-2.5 py-1 text-xs font-medium hover:bg-black/5 dark:hover:bg-white/10 disabled:opacity-50"
        >
          {capture.enabled ? 'Disarm' : 'Arm for 1 hour'}
        </button>
      </div>
      <span className="text-xs text-black/50 dark:text-white/50">
        While armed, webhook deliveries to this integration keep a copy of what came in and what it mapped to, so you
        can inspect one below. Off by default, self-expires after an hour, and anything credential-shaped (password,
        secret, token, key…) is redacted before it's stored.
      </span>
      {error && <span className="text-xs text-red-500">{error}</span>}
    </AppCard>
  );
}

// One run row; expands (webhook runs only - see DebugCaptureControl) to show
// its captured raw+mapped sample, if capture was armed when it happened.
function RunRow({
  nickname,
  run,
  expanded,
  onToggle,
}: {
  nickname: string;
  run: IntegrationRun;
  expanded: boolean;
  onToggle: () => void;
}) {
  const [sample, setSample] = useState<DebugSample | null | undefined>(undefined); // undefined = not fetched yet
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const canExpand = run.source === 'webhook';

  const handleToggle = () => {
    onToggle();
    if (canExpand && sample === undefined && !loading) {
      setLoading(true);
      setError(null);
      fetchDebugSample(nickname, run.id)
        .then(setSample)
        .catch((e) => setError(e instanceof Error ? e.message : 'Unable to load the debug sample.'))
        .finally(() => setLoading(false));
    }
  };

  return (
    <div className="flex flex-col">
      <button
        type="button"
        onClick={handleToggle}
        disabled={!canExpand}
        className={`flex items-start gap-2 py-2 text-xs text-left ${canExpand ? 'cursor-pointer hover:bg-black/[0.02] dark:hover:bg-white/[0.03]' : 'cursor-default'}`}
      >
        <span className="w-4 shrink-0 pt-0.5 text-black/30 dark:text-white/30">
          {canExpand ? expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} /> : null}
        </span>
        <span className={`w-16 shrink-0 font-medium ${statusColor[run.status]}`}>{runStatusLabel[run.status]}</span>
        <div className="flex flex-col min-w-0 flex-1">
          <span className="text-black/70 dark:text-white/70">
            {runSourceLabel[run.source] ?? run.source} · {describeRunCounts(run)}
          </span>
          {run.error && <span className="text-black/45 dark:text-white/45">{run.error.message}</span>}
        </div>
        <span className="shrink-0 text-black/40 dark:text-white/40">{formatRunTime(run.startedAt)}</span>
      </button>
      {expanded && canExpand && (
        <div className="ml-6 mb-2 flex flex-col gap-2 rounded-lg bg-black/[0.03] dark:bg-white/[0.04] p-2.5">
          {loading && (
            <span className="text-black/40 dark:text-white/40">
              <Loader2 size={12} className="inline animate-spin" /> Loading sample…
            </span>
          )}
          {error && <span className="text-red-500">{error}</span>}
          {!loading && !error && sample === null && (
            <span className="text-black/45 dark:text-white/45">
              No sample was captured for this run - capture wasn't armed at the time, or it has since expired.
            </span>
          )}
          {!loading && !error && sample && (
            <>
              <div className="flex flex-col gap-1">
                <span className="text-[11px] font-semibold text-black/50 dark:text-white/50">
                  What came in (redacted)
                </span>
                <pre className="m-0 overflow-x-auto whitespace-pre-wrap break-all font-mono text-[11px] text-black/70 dark:text-white/70">
                  {JSON.stringify(sample.sample, null, 2)}
                </pre>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-[11px] font-semibold text-black/50 dark:text-white/50">What it mapped to</span>
                {sample.mapped.length === 0 ? (
                  <span className="text-black/45 dark:text-white/45">Nothing - no Signal was produced.</span>
                ) : (
                  sample.mapped.map((m, i) => (
                    <div key={i} className="flex flex-wrap items-baseline gap-1.5 font-mono text-[11px]">
                      <span className="font-semibold">{m.name}</span>
                      <span>= {m.value || '(none)'}</span>
                      {m.labels.length > 0 && <span className="text-black/45 dark:text-white/45">[{m.labels.join(', ')}]</span>}
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// Where this account's integration health Signals go. Never the
// integration's own destination channel by default - the owner picks a
// channel they own (ideally a private one dedicated to this).
export function HealthAlertsControl() {
  const { channels, isChannelOwner } = useStore();
  const [channelId, setChannelId] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    fetchIntegrationAlertChannel()
      .then((c) => alive && setChannelId(c))
      .finally(() => alive && setLoaded(true));
    return () => {
      alive = false;
    };
  }, []);

  if (!loaded) return null;
  const owned = channels.filter((c) => isChannelOwner(c));

  const change = async (next: string) => {
    setSaving(true);
    setError(null);
    try {
      setChannelId(await setIntegrationAlertChannel(next));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppCard className="flex flex-col gap-1.5">
      <span className="text-sm font-semibold">Health alerts</span>
      <span className="text-xs text-black/50 dark:text-white/50">
        Post a message to a channel when an integration needs attention, is delayed, or recovers. Pick a private
        channel of your own - alerts are never sent to an integration’s data channel.
      </span>
      <select
        aria-label="Health alerts channel"
        value={channelId}
        disabled={saving}
        onChange={(e) => void change(e.target.value)}
        className="rounded-lg border border-black/15 dark:border-white/15 bg-white dark:bg-white/5 px-2.5 py-1.5 text-sm outline-none focus:border-blue-500 disabled:opacity-50"
      >
        <option value="">Off</option>
        {owned.map((c) => (
          <option key={c.id} value={c.nickname}>
            {c.title || c.nickname}
          </option>
        ))}
      </select>
      {owned.length === 0 && (
        <span className="text-[11px] text-black/40 dark:text-white/40">
          You don’t own a channel yet. <Link to="/channels/new" className="underline">Create one</Link> to receive alerts.
        </span>
      )}
      {error && <span className="text-xs text-red-500">{error}</span>}
    </AppCard>
  );
}
